import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRetailerWholesalerVendorOrders } from '../../actions/retailerWholesalerVendorOrdersActions'; // Update the action import
import { useHistory } from 'react-router-dom';
import useOrderListFilters from '../../hooks/useOrderListFilters';
import OrderListFilterBar from '../../components/Orders/OrderListFilterBar';
import {
    Container,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    makeStyles,
    TablePagination,
    Button,
    Chip,
    Box,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { getUserFromLocalStorage } from '../../utilities/user.js';
import { PaymentBadge } from '../../components/OrderStatusBadges';

// Last 8 characters, the same short form the customer-facing order confirmation
// shows, so support and vendors quote the same code back to each other.
const shortId = (id) => (id ? String(id).slice(-8).toUpperCase() : '—');

const fmtMoney = (n) => {
    try {
        return new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX' }).format(Number(n) || 0);
    } catch (_) {
        return `UGX ${Number(n) || 0}`;
    }
};

// Date only. toLocaleString() added a time that nothing on this screen needs and
// that wrapped the column onto three lines at phone widths.
const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return Number.isNaN(dt.getTime())
        ? '—'
        : dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const RESPONSIVE_CSS = `
.rwot-table-wrap { overflow-x: auto; }
.rwot-id { font-family: monospace; font-weight: 700; letter-spacing: 0.03em; }
.rwot-card-list { display: none; }

@media (max-width: 768px) {
  .rwot-table-wrap { display: none; }
  .rwot-card-list { display: block; }

  .rwot-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-left: 4px solid #003547;
    border-radius: 10px;
    padding: 13px 14px;
    margin-bottom: 10px;
    cursor: pointer;
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
  }
  .rwot-card:active { background: #fbfcfd; }
  .rwot-card-top {
    display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
  }
  .rwot-card-top .rwot-id { font-size: 0.86rem; color: #1f2937; }
  .rwot-amount {
    font-size: 1rem; font-weight: 800; color: #003547;
    font-variant-numeric: tabular-nums; white-space: nowrap;
  }
  .rwot-card-badges {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 9px;
  }
  .rwot-pay {
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
    color: #4a5568; background: #eef1f4; border-radius: 999px; padding: 3px 9px;
  }
  .rwot-card-foot {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    margin-top: 11px; padding-top: 10px; border-top: 1px solid #f1f3f5;
    font-size: 0.76rem; color: #8a9aa9;
  }
  /* Filled rather than coloured text: this is the card's action, and it now
     carries the same #003547 the desktop "Manage Order" button uses, so the two
     read as the same control. As plain navy text it sat at the same visual
     weight as the order id and the amount and did not look tappable. */
  .rwot-manage {
    font-size: 0.76rem;
    font-weight: 700;
    color: #fff;
    background: #003547;
    border-radius: 999px;
    padding: 7px 15px;
    white-space: nowrap;
    box-shadow: 0 1px 3px rgba(0, 53, 71, 0.32);
  }
  .rwot-card:active .rwot-manage { background: #00252f; }
}
`;

const useStyles = makeStyles((theme) => ({
    table: {
        minWidth: 650,
        border: `1px solid ${theme.palette.divider}`,
    },
    container: {
        marginTop: theme.spacing(4),
        padding: theme.spacing(2),
        backgroundColor: '#f5f5f5',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
    },
    error: {
        color: theme.palette.error.main,
        textAlign: 'center',
        marginBottom: theme.spacing(2),
    },
    tableHead: {
        backgroundColor: theme.palette.primary.main,
    },
    tableHeadCell: {
        color: theme.palette.common.white,
        fontWeight: 'bold',
    },
    tableRow: {
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    pagination: {
        marginTop: theme.spacing(2),
    },
    chipProcessing: {
        backgroundColor: '#ff9800',
        color: '#fff',
    },
    chipAssigned: {
        backgroundColor: '#3f51b5',
        color: '#fff',
    },
    chipDispatched: {
        backgroundColor: '#2196f3',
        color: '#fff',
    },
    chipDelivered: {
        backgroundColor: '#4caf50',
        color: '#fff',
    },
    chipRejected: {
        backgroundColor: '#f44336',
        color: '#fff',
    },
    noOrdersContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        padding: theme.spacing(4),
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        marginTop: theme.spacing(4),
    },
    noOrdersIcon: {
        width: '280px',
        height: '280px',
        marginBottom: theme.spacing(4),
        animation: '$float 3s ease-in-out infinite',
    },
    '@keyframes float': {
        '0%, 100%': {
            transform: 'translateY(0px)',
        },
        '50%': {
            transform: 'translateY(-20px)',
        },
    },
    noOrdersTitle: {
        fontSize: '2rem',
        fontWeight: 700,
        color: '#2d3748',
        marginBottom: theme.spacing(2),
        letterSpacing: '-0.5px',
    },
    noOrdersSubtitle: {
        fontSize: '1.1rem',
        color: '#718096',
        marginBottom: theme.spacing(4),
        maxWidth: '500px',
        lineHeight: 1.6,
    },
    noOrdersHint: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(2, 3),
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: '12px',
        marginTop: theme.spacing(2),
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    noOrdersHintIcon: {
        fontSize: '1.5rem',
    },
}));

const RetailerWholesalerOrderTable = () => {
    const classes = useStyles();
    const [vendorId, setVendorId] = useState(null);
    const dispatch = useDispatch();
    const { orders, loading, error, currentPage, numberOfPages } = useSelector((state) => state.retailerWholesalerVendorOrders); // Update the state selector
    const history = useHistory();

    const user = getUserFromLocalStorage();
    // Status and payment-method filters live in the URL: the dashboard cards
    // open this list with one set (utilities/orderDeepLinks.js) and the
    // server applies it on this shop's own line items, the same way the
    // dashboard counted, so the card's number is this list's length.
    const listFilters = useOrderListFilters();

    useEffect(() => {
        if (user) {
            setVendorId(user.result._id); // Ensure this is the _id of the owner
        }
    }, [user]);

    // A changed filter restarts at page 1: the page the operator was on may
    // not exist for the narrower list.
    const lastFilterKey = useRef(listFilters.filterKey);
    useEffect(() => {
        if (!vendorId) return;
        const filterChanged = lastFilterKey.current !== listFilters.filterKey;
        lastFilterKey.current = listFilters.filterKey;
        dispatch(fetchRetailerWholesalerVendorOrders(vendorId, filterChanged ? 1 : currentPage, 10, listFilters.filters));
    }, [dispatch, vendorId, currentPage, listFilters.filterKey]);

    const getChipClass = (status) => {
        switch (status) {
            case 'PROCESSING':
                return classes.chipProcessing;
            case 'ASSIGNED':
                return classes.chipAssigned;
            case 'DISPATCHED':
                return classes.chipDispatched;
            case 'DELIVERED':
                return classes.chipDelivered;
            case 'REJECTED':
                return classes.chipRejected;
            default:
                return '';
        }
    };

    if (loading) return <div className={classes.loadingContainer}><CircularProgress /></div>;
    if (error) return (
        <div>
            <Alert severity="error" className={classes.error}>Error: {error}</Alert>
        </div>
    );

    return (
        <Container className={classes.container}>
            <style>{RESPONSIVE_CSS}</style>
            <Typography variant="h4" gutterBottom>{user?.result?.businessName} Orders</Typography>
            <OrderListFilterBar
                delivery={listFilters.delivery}
                method={listFilters.method}
                onDelivery={listFilters.setDelivery}
                onMethod={listFilters.setMethod}
            />
            {orders.length === 0 && listFilters.active > 0 ? (
                <Box className={classes.noOrdersContainer}>
                    <Typography className={classes.noOrdersTitle}>No orders match this filter</Typography>
                    <Typography className={classes.noOrdersSubtitle}>
                        Choose Any status, or remove the payment method, to see every order.
                    </Typography>
                </Box>
            ) : orders.length === 0 ? (
                <Box className={classes.noOrdersContainer}>
                    <svg className={classes.noOrdersIcon} viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                        {/* Background Circle */}
                        <circle cx="250" cy="250" r="200" fill="#E8F4F8" opacity="0.5"/>
                        
                        {/* Decorative Elements */}
                        <circle cx="100" cy="100" r="20" fill="#0291a1" opacity="0.2"/>
                        <circle cx="400" cy="120" r="15" fill="#005370" opacity="0.2"/>
                        <circle cx="380" cy="380" r="25" fill="#0291a1" opacity="0.15"/>
                        
                        {/* Shopping Cart */}
                        <g transform="translate(150, 150)">
                            {/* Cart Body */}
                            <path d="M20 40 L40 40 L60 120 L160 120 L180 60 L50 60" 
                                  stroke="#005370" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            
                            {/* Cart Handle */}
                            <path d="M20 40 L10 10" stroke="#005370" strokeWidth="8" strokeLinecap="round"/>
                            
                            {/* Wheels */}
                            <circle cx="80" cy="140" r="12" fill="#0291a1"/>
                            <circle cx="140" cy="140" r="12" fill="#0291a1"/>
                            
                            {/* Items in cart */}
                            <rect x="70" y="70" width="30" height="40" rx="4" fill="#E8F4F8" stroke="#005370" strokeWidth="3"/>
                            <rect x="110" y="65" width="35" height="45" rx="4" fill="#E8F4F8" stroke="#005370" strokeWidth="3"/>
                        </g>
                        
                        {/* Empty Box Icon */}
                        <g transform="translate(280, 200)">
                            <path d="M0 20 L40 0 L80 20 L80 80 L40 100 L0 80 Z" 
                                  fill="#f8f9fa" stroke="#005370" strokeWidth="4" strokeLinejoin="round"/>
                            <path d="M0 20 L40 40 L80 20" stroke="#005370" strokeWidth="4" strokeLinejoin="round"/>
                            <path d="M40 40 L40 100" stroke="#005370" strokeWidth="4"/>
                            
                            {/* X mark inside box */}
                            <line x1="20" y1="50" x2="60" y2="80" stroke="#0291a1" strokeWidth="3" strokeLinecap="round"/>
                            <line x1="60" y1="50" x2="20" y2="80" stroke="#0291a1" strokeWidth="3" strokeLinecap="round"/>
                        </g>
                        
                        {/* Floating Dots */}
                        <circle cx="120" cy="350" r="8" fill="#0291a1" opacity="0.4">
                            <animate attributeName="cy" values="350;330;350" dur="2s" repeatCount="indefinite"/>
                        </circle>
                        <circle cx="370" cy="280" r="6" fill="#005370" opacity="0.4">
                            <animate attributeName="cy" values="280;260;280" dur="2.5s" repeatCount="indefinite"/>
                        </circle>
                    </svg>
                    <Typography className={classes.noOrdersTitle}>
                        No Orders Yet
                    </Typography>
                    <Typography className={classes.noOrdersSubtitle}>
                        Your order history is empty. Once customers start placing orders, they'll appear here for you to manage and fulfill.
                    </Typography>
                    <Box className={classes.noOrdersHint}>
                        <span className={classes.noOrdersHintIcon}>💡</span>
                        <Typography variant="body2" style={{ color: '#4a5568', fontWeight: 500 }}>
                            Make sure your products are active and visible to customers
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <>
                    {/* Six columns at minWidth 650 inside a 336px-wide phone
                        viewport. MUI's TableContainer does set overflow-x:auto,
                        so this was technically scrollable — but with no visible
                        scrollbar on touch, no edge shadow and no sticky first
                        column, a sideways swipe over the rows reads as a page
                        scroll and everything past Total Price is effectively
                        unreachable. Below 768px the table is swapped for a card
                        per order, matching Tables/PaginatedOrderTable.js and
                        views/RetailersWholesalers.js. */}
                    <div className="rwot-table-wrap">
                        <TableContainer component={Paper}>
                            <Table className={classes.table} aria-label="orders">
                                <TableHead className={classes.tableHead}>
                                    <TableRow>
                                        <TableCell className={classes.tableHeadCell}>Order ID</TableCell>
                                        <TableCell className={classes.tableHeadCell}>Total Price</TableCell>
                                        <TableCell className={classes.tableHeadCell} align="center">Payment Method</TableCell>
                                        <TableCell className={classes.tableHeadCell} align="center">Payment</TableCell>
                                        <TableCell className={classes.tableHeadCell}>Delivery Status</TableCell>
                                        <TableCell className={classes.tableHeadCell}>Order Date</TableCell>
                                        <TableCell className={classes.tableHeadCell}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {orders.map((order) => (
                                        <TableRow key={order._id} className={classes.tableRow}>
                                            {/* The full 24-char ObjectId ate ~200px of a 336px row.
                                                Short code up front, full id kept in the title for
                                                anyone who needs to copy it. */}
                                            <TableCell component="th" scope="row" title={order._id}>
                                                <span className="rwot-id">{shortId(order._id)}</span>
                                            </TableCell>
                                            <TableCell>{fmtMoney(order.totalPrice)}</TableCell>
                                            <TableCell align="center">{order.paymentMethod}</TableCell>
                                            {/* Paid/unpaid, derived from the canonical
                                                payment fields (utilities/paymentStatus.js). */}
                                            <TableCell align="center"><PaymentBadge order={order} /></TableCell>
                                            <TableCell>
                                                <Chip label={order.delivery_status} className={getChipClass(order.delivery_status)} />
                                            </TableCell>
                                            <TableCell>{fmtDate(order.createdAt)}</TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="outlined"
                                                    style={{ color: '#fff', backgroundColor: '#003547' }}
                                                    size="small"
                                                    onClick={() => history.push(`/admin/retWholesalerOrderDetails/${order._id}`)}
                                                >
                                                    Manage Order
                                                    <i style={{ marginLeft: '5px' }} className="fas fa-edit"></i>
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>

                    <div className="rwot-card-list">
                        {orders.map((order) => (
                            <div
                                key={order._id}
                                className="rwot-card"
                                role="button"
                                tabIndex={0}
                                onClick={() => history.push(`/admin/retWholesalerOrderDetails/${order._id}`)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        history.push(`/admin/retWholesalerOrderDetails/${order._id}`);
                                    }
                                }}
                            >
                                <div className="rwot-card-top">
                                    <span className="rwot-id" title={order._id}>{shortId(order._id)}</span>
                                    <span className="rwot-amount">{fmtMoney(order.totalPrice)}</span>
                                </div>
                                <div className="rwot-card-badges">
                                    <Chip
                                        size="small"
                                        label={order.delivery_status}
                                        className={getChipClass(order.delivery_status)}
                                    />
                                    <PaymentBadge order={order} />
                                    {!!order.paymentMethod && (
                                        <span className="rwot-pay">{order.paymentMethod}</span>
                                    )}
                                </div>
                                <div className="rwot-card-foot">
                                    <span>{fmtDate(order.createdAt)}</span>
                                    <span className="rwot-manage">Manage order &rarr;</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
            <TablePagination
                component="div"
                count={numberOfPages * 10}
                page={currentPage - 1}
                onPageChange={(event, newPage) => dispatch(fetchRetailerWholesalerVendorOrders(vendorId, newPage + 1, 10, listFilters.filters))}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
                className={classes.pagination}
            />
        </Container>
    );
};

export default RetailerWholesalerOrderTable;
